import { useEffect, useRef } from 'react';

interface Particle {
  x: number;
  y: number;
  vx: number;
  vy: number;
  size: number;
  baseOpacity: number;
  hue: number;
  phase: number;
  driftAmp: number;
  driftFreq: number;
}

const EnergyParticles = () => {
  const canvasRef = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reducedMotion) return;

    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    let animationFrameId: number;
    let particles: Particle[] = [];
    let isPaused = document.hidden;
    let scrollTimeout: number;
    const isMobile = window.innerWidth < 768;

    const onVisibility = () => { isPaused = document.hidden; };
    const onScroll = () => {
      isPaused = true;
      clearTimeout(scrollTimeout);
      scrollTimeout = window.setTimeout(() => { isPaused = false; }, 120);
    };

    const resize = () => {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = window.innerWidth * dpr;
      canvas.height = window.innerHeight * dpr;
      canvas.style.width = window.innerWidth + 'px';
      canvas.style.height = window.innerHeight + 'px';
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      init();
    };

    const init = () => {
      particles = [];
      const num = isMobile ? 18 : 40;
      for (let i = 0; i < num; i++) {
        particles.push({
          x: Math.random() * window.innerWidth,
          y: Math.random() * window.innerHeight,
          vx: (Math.random() - 0.5) * 0.15,
          vy: -0.05 - Math.random() * 0.1,
          size: 0.8 + Math.random() * 2.2,
          baseOpacity: 0.25 + Math.random() * 0.4,
          hue: Math.random() < 0.65 ? 322 : 275, // magenta or violet
          phase: Math.random() * Math.PI * 2,
          driftAmp: 0.3 + Math.random() * 0.6,
          driftFreq: 0.0008 + Math.random() * 0.0012,
        });
      }
    };

    const targetFPS = 24;
    const frameInterval = 1000 / targetFPS;
    let lastFrame = 0;

    const animate = (t: number) => {
      animationFrameId = requestAnimationFrame(animate);
      if (isPaused) { lastFrame = t; return; }
      const elapsed = t - lastFrame;
      if (elapsed < frameInterval) return;
      lastFrame = t - (elapsed % frameInterval);

      ctx.clearRect(0, 0, window.innerWidth, window.innerHeight);

      for (const p of particles) {
        // Curved drift via sin offset
        p.x += p.vx + Math.sin(t * p.driftFreq + p.phase) * p.driftAmp * 0.05;
        p.y += p.vy;

        // Wrap
        if (p.y < -10) { p.y = window.innerHeight + 10; p.x = Math.random() * window.innerWidth; }
        if (p.x < -10) p.x = window.innerWidth + 10;
        if (p.x > window.innerWidth + 10) p.x = -10;

        const opacity = p.baseOpacity + Math.sin(t * 0.001 + p.phase) * 0.15;
        ctx.globalAlpha = Math.max(0.05, opacity);

        // Soft glow
        const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, p.size * 6);
        grad.addColorStop(0, `hsla(${p.hue}, 100%, 70%, 0.9)`);
        grad.addColorStop(0.4, `hsla(${p.hue}, 100%, 60%, 0.3)`);
        grad.addColorStop(1, `hsla(${p.hue}, 100%, 50%, 0)`);
        ctx.fillStyle = grad;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * 6, 0, Math.PI * 2);
        ctx.fill();

        // Bright core
        ctx.fillStyle = `hsla(${p.hue}, 100%, 85%, 0.95)`;
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size * 0.6, 0, Math.PI * 2);
        ctx.fill();
      }
      ctx.globalAlpha = 1;
    };

    document.addEventListener('visibilitychange', onVisibility);
    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', resize);
    resize();
    animate(0);

    return () => {
      document.removeEventListener('visibilitychange', onVisibility);
      window.removeEventListener('scroll', onScroll as EventListener);
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  return (
    <canvas
      ref={canvasRef}
      aria-hidden="true"
      role="presentation"
      className="absolute top-0 left-0 w-full h-full pointer-events-none"
      style={{ zIndex: 2, willChange: 'transform', transform: 'translateZ(0)' }}
    />
  );
};

export default EnergyParticles;
